<template>
  <v-container>
    <FormRow 
      label="Enable Automatic Firmware Updates"
      :input-payload="enableAFU"
      :check-box="true"
      @parseNewVAl="parseNewVal($event, 'enableAFU', 'Firmware Update / Enable Automatic Firmware Updates')"
    />

    <FormRow
      label="Check for update every"
      :input-payload="updateCheckInterval"
      :dropdown="true"
      :options="updateCheckIntervalList"
      @parseNewVAl="parseNewValInterval($event, 'updateCheckInterval', 'Firmware Update / Check for Update Every')"
    />

    <FormRow
      v-if="supportsPreferredUpdateTime"
      label="Apply update"
      :input-payload="applyUpdateOn"
      :dropdown="true"
      :options="applyUpdateOnList"
      @parseNewVAl="parseNewValUpdateOn($event, 'applyUpdateOn', 'Firmware Update / Apply Update on')"
    />

    <v-row v-if="supportsPreferredUpdateTime">
      <v-col cols="4" class="no-padding align-vertical pl-2">
        <div>Update time</div>
      </v-col>
      <v-col cols="8" class="no-padding align-vertical">
        <vue-timepicker v-if="hasTimeBeenSet" v-model="time" :disabled="disableTimeSelect" @change="setTime"></vue-timepicker>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>

import FormRow from '../components/FormRow.vue';

import {deviceConfigMixin} from '@/mixins/deviceConfigMixin.js';

import VueTimepicker from 'vue2-timepicker/src/vue-timepicker.vue'

// import { Logger } from "aws-amplify";
// const logger = new Logger("device config / firmware update");

export default {
  name: 'FirmwareUpdate',

  components: {
    FormRow,
    VueTimepicker
  },

  mixins: [deviceConfigMixin],

  props: {
    config: {
      type: Object,
      required: true
    },
    firmwareVersion: {
      type: String,
      required: true
    },
    deviceModel: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      time: {
        HH: '',
        mm: ''
      },
      manualTimeChange: 0,
      updateCheckIntervalList: [
        {
          xml: '1',
          label: '1 Day'
        },
        {
          xml: '2',
          label: '2 Days'
        },
        {
          xml: '3',
          label: '3 Days'
        },
        {
          xml: '4',
          label: '4 Days'
        },
        {
          xml: '5',
          label: '5 Days'
        },
        {
          xml: '6',
          label: '6 Days'
        },
        {
          xml: '7',
          label: '7 Days'
        }
      ],
      applyUpdateOnList: [
        {
          xml: 'asap',
          label: 'As soon as possible '
        },
        {
          xml: 'accOff',
          label: 'On ACC off'
        },
        {
          xml: 'timeOfDay',
          label: 'At time of day'
        }
      ],
      updateOn: ''
    }
  },

  computed: {
    enableAFU() {
      return JSON.parse(this.config.device_config.system[0].autoUpdate[0].enabled[0])
    },
    hasTimeBeenSet() {
      return this.time.HH != '' || this.time.mm != '' ? true : false;
    },
    updateCheckInterval() {
      return this.config.device_config.system[0].autoUpdate[0].updateCheckInterval[0]
    },

    supportsPreferredUpdateTime() {
      return Object.hasOwn(this.config.device_config.system[0].autoUpdate[0], 'preferredUpdateTime')
    },

    applyUpdateOn() {
      return this.supportsPreferredUpdateTime ? this.config.device_config.system[0].autoUpdate[0].preferredUpdateTime[0].selectedOption[0] : ''
    },

    updateTime() {
      return this.supportsPreferredUpdateTime ? this.config.device_config.system[0].autoUpdate[0].preferredUpdateTime[0].timeOfDayUpdate[0] : ''
    },

    disableTimeSelect() {
      return this.updateOn != 'timeOfDay' ? true : false
    }
  },

  watch: {
    updateOn() {
      if(this.updateOn != 'timeOfDay') {
        let payload = {
          newValue: 'removePropertyFromConfigObject',
          key: this.configPropMapper('updateTime'),
          label: 'Firmware Update / Update Time'
        }
        
        this.$emit('settingsUpdated', payload, this.mapList)
      }
    }
  },
  

  mounted() {
    this.convertMinstoHrs(this.updateTime);
    if(this.supportsPreferredUpdateTime) {
      this.updateOn = this.applyUpdateOn
    }
    
  },

  methods: {
    setTime() {
      this.manualTimeChange++;
      const time = this.convertHrstoMins();

      // Prevent the global config object from being updated on component mount 
      // as it was listing the change in review config modal
      if (this.manualTimeChange < 2) return;
      this.parseNewVal(time, 'updateTime', 'Firmware Update / Update Time');
    },

    parseNewVal(val, id, label) {
      let payload = {
        newValue: this.returnMappedVal(val, []),
        key: this.configPropMapper(id),
        label: label
      }

      this.$emit('settingsUpdated', payload);
    },

    parseNewValUpdateOn(val, id, label) {
      let payload = {
        newValue: this.returnMappedVal(val, [this.applyUpdateOnList]),
        key: this.configPropMapper(id),
        label: label
      }
    
      this.updateOn = this.returnMappedVal(val, [this.applyUpdateOnList])
      this.$emit('settingsUpdated', payload);
    },

    parseNewValInterval(val, id, label) {
      let payload = {
        newValue: this.returnMappedVal(val, [this.updateCheckIntervalList]),
        key: this.configPropMapper(id),
        label: label
      }

      this.$emit('settingsUpdated', payload);
    },

    configPropMapper(key) {
      let map = {
        'enableAFU': `device_config.system[0].autoUpdate[0].enabled[0]`,
        'updateCheckInterval': `device_config.system[0].autoUpdate[0].updateCheckInterval[0]`,
        'applyUpdateOn': `device_config.system[0].autoUpdate[0].preferredUpdateTime[0].selectedOption[0]`,
        'updateTime': `device_config.system[0].autoUpdate[0].preferredUpdateTime[0].timeOfDayUpdate[0]`
      }

      return map[key];
    },

    checkEmptyTimeString(time) {
      return time ? time : '00';
    },

    convertHrstoMins() {
      let hms = parseInt(this.checkEmptyTimeString(this.time.HH)) + ':' + parseInt(this.checkEmptyTimeString(this.time.mm));
      let a = hms.split(':'); // split it at the colons

      // Hours are worth 60 minutes.
      let minutes = (+a[0]) * 60 + (+a[1]);

      return minutes;
    },

    convertMinstoHrs(minutes) {
      var h = Math.floor(minutes / 60);
      var m = minutes % 60;
      
      h = h < 10 ? '0' + h : h;
      m = m < 10 ? '0' + m : m;

      this.time.HH = h.toString();
      this.time.mm = m.toString();
    },
  }
}
</script>